import { styled } from "@mui/material/styles";
import {
  AcquireBookDataGrid,
  buildSelfAcquiredInitialState,
  OTHER_USER_RESOURCE_FIELD,
  SELF_ACQUIRED_OPERATOR_VALUE,
  OTHER_USER_ACQUIRED_OPERATOR_VALUE,
} from "@/components/DataGrid";
import {
  type GridRowId,
  type GridColDef,
  type GridFilterOperator,
  type GridRenderCellParams,
  type GridValueFormatterParams,
} from "@mui/x-data-grid-pro";
import { useApiClient } from "@/hooks/useApiClient";
import useSWR from "swr";
import { ReadOnlyTooltip } from "@/components/Tooltip/ReadOnlyTooltip";
import { Button, Stack, Tooltip } from "@mui/material";
import OutputMenu from "@/features/ownerInfo/components/OutputMenu";
import { format } from "date-fns";
import { isActionEnabled, statusToLabel } from "@/features/ownerInfo/utils";
import { type RequestDetailAndRealEstates } from "@/features/ownerInfo/types";
import CircularProgress from "@mui/material/CircularProgress";
import CheckIcon from "@mui/icons-material/Check";
import React, {
  forwardRef,
  type ForwardRefRenderFunction,
  useImperativeHandle,
  useContext,
  useEffect,
  useState,
} from "react";
import { ButtonVariantOption, LinkButton } from "@/components/Button";
import { SizingWrapperStyle } from "@/components/Wrapper";
import { formatNumberWithComma } from "@/utils/formatNumber";
import { useFeatureFlags } from "@/configs/featureFlag";
import { type AxiosError, HttpStatusCode } from "axios";
import { SendToTrackingService } from "@/api/handlers/errors";
import { useReadOnlyUser } from "@/hooks/useReadOnlyUser";
import * as Sentry from "@sentry/react";
import { z } from "zod";
import { userDataContext } from "@/hooks/useUserData";
import { useShowSendToSamboKun } from "@/features/partner/hooks/useShowSendToSamboKun";
import { useSamboKunLimit } from "@/features/partner/hooks/useSamboKunLimit";
import {
  getSamboKunSendDisableReason,
  samboKunSendDisabledTooltip,
} from "@/features/partner/utils/isSendDisabled";
import { SendToSamboKunDialog } from "@/features/partner/components/SendToSamboKunDialog";

interface OwnerInfoListGridRow extends RequestDetailAndRealEstates {
  action?: boolean;
}

interface OwnerInfoListAPIResponse {
  count: number;
  list: OwnerInfoListGridRow[];
}

const otherUserResourceFilterOperators: Array<
  GridFilterOperator<OwnerInfoListGridRow, boolean>
> = [
  {
    label: "自分で取得",
    value: SELF_ACQUIRED_OPERATOR_VALUE,
    requiresFilterValue: false,
    getApplyFilterFn:
      () =>
      ({ row }) =>
        !row.isOtherUserResource,
  },
  {
    label: "他ユーザー取得",
    value: OTHER_USER_ACQUIRED_OPERATOR_VALUE,
    requiresFilterValue: false,
    getApplyFilterFn:
      () =>
      ({ row }) =>
        row.isOtherUserResource,
  },
];

const ownerAnalyzeListContractSchema = z
  .object({
    list: z.array(
      z
        .object({
          isOtherUserResource: z.boolean(),
          hasTriggerPictureRequest: z.boolean(),
        })
        .passthrough()
    ),
  })
  .passthrough();

const validateOwnerAnalyzeListContract = (
  payload: unknown,
  endpoint: string
): void => {
  const parsed = ownerAnalyzeListContractSchema.safeParse(payload);
  if (parsed.success) return;

  const error = new Error(`API contract violation at ${endpoint}`);
  Sentry.captureException(error, {
    extra: {
      endpoint,
      issues: parsed.error.issues,
    },
  });
  throw error;
};

interface IProps {
  setSelectedId: React.Dispatch<React.SetStateAction<GridRowId>>;
  setOpenMonitoringExecuteConfirmDialog: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  setChargeCountClickedMonitoringExecuteButton: React.Dispatch<
    React.SetStateAction<number | null>
  >;
}

const StyledDataGrid = styled(AcquireBookDataGrid)`
  // テーブルのセルをクリックしても青い枠が表示されないようにする
  & .MuiDataGrid-cell:focus-within,
  & .MuiDataGrid-cell:focus {
    outline: none !important;
  }

  // テーブルのカラムヘッダをクリックしても青い枠が表示されないようにする
  & .MuiDataGrid-columnHeader:focus-within,
  & .MuiDataGrid-columnHeader:focus {
    outline: none !important;
  }

  // テーブルヘッダーの背景色を設定
  .MuiDataGrid-columnHeaders {
    line-height: 1.5em !important;
  }

  // テーブルタイトルを太字に
  .MuiDataGrid-columnHeaderTitle {
    font-weight: 700;
  }
`;

const makeGridColDef = (
  realEstateRegistrationMonitoring: boolean,
  isReadOnly: boolean,
  isSharableUser: boolean,
  setSelectedId: React.Dispatch<React.SetStateAction<GridRowId>>,
  setAcquireBookOpen: React.Dispatch<React.SetStateAction<boolean>>,
  setChargeCountClickedMonitoringExecuteButton: React.Dispatch<
    React.SetStateAction<number | null>
  >,
  onDownloadComplete: () => void,
  showSendToSamboKun: boolean,
  onClickSendToSamboKun: (rowId: number) => void,
  maxPropertiesPerRequest: number | null,
  isLimitError: boolean
): GridColDef[] => {
  const BASE_ACTION_COLUMN_WIDTH = 240;
  const MONITORING_BUTTON_WIDTH = 160;
  const SAMBO_KUN_BUTTON_WIDTH = 150;
  const actionColumnMinWidth =
    BASE_ACTION_COLUMN_WIDTH +
    (realEstateRegistrationMonitoring ? MONITORING_BUTTON_WIDTH : 0) +
    (showSendToSamboKun ? SAMBO_KUN_BUTTON_WIDTH : 0);

  return [
    {
      field: "taskId",
      headerName: "識別番号",
      width: 110,
      sortable: false,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "identificationName",
      headerName: "識別名",
      width: 280,
      sortable: false,
      align: "left",
      headerAlign: "left",
      renderCell: ({
        value,
      }: GridRenderCellParams<string, OwnerInfoListGridRow>) => value ?? "",
    },
    {
      field: "succeedCount",
      headerName: "アップロード件数",
      width: 150,
      sortable: false,
      align: "right",
      headerAlign: "right",
      renderCell: ({
        value,
      }: GridRenderCellParams<number, OwnerInfoListGridRow>) =>
        formatNumberWithComma(value),
    },
    {
      field: "createdAt",
      type: "dateTime",
      headerName: "アップロード日時",
      width: 220,
      sortable: false,
      align: "center",
      headerAlign: "center",
      valueFormatter: (params: GridValueFormatterParams<string>) => {
        const value = params.value;
        if (value == null) return "";
        return format(new Date(value), "yyyy/MM/dd HH:mm");
      },
    },
    {
      field: OTHER_USER_RESOURCE_FIELD,
      headerName: "他ユーザー取得",
      width: 140,
      filterOperators: otherUserResourceFilterOperators,
      hide: !isSharableUser,
      sortable: false,
      align: "center",
      headerAlign: "center",
      valueFormatter: () => "",
      renderCell: ({
        value,
      }: GridRenderCellParams<boolean, OwnerInfoListGridRow>) =>
        value ? <CheckIcon color="action" fontSize="small" /> : "",
    },
    {
      field: "status",
      headerName: "解析状況",
      width: 120,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }: GridRenderCellParams<string>) => {
        return statusToLabel(value);
      },
    },
    {
      field: "action",
      headerName: "Action",
      minWidth: actionColumnMinWidth,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: ({
        id,
        row,
      }: GridRenderCellParams<string, OwnerInfoListGridRow>) => {
        return (
          <Stack direction="row" spacing={2}>
            <LinkButton
              disabled={!isActionEnabled(row.status)}
              variant={ButtonVariantOption.Contained}
              wrapperSize={SizingWrapperStyle.INHERIT}
              href={`/ownerinfo/detail/${id}/`}
            >
              詳細
            </LinkButton>
            {realEstateRegistrationMonitoring ? (
              <ReadOnlyTooltip isReadOnly={isReadOnly}>
                <Button
                  variant={ButtonVariantOption.Outlined}
                  onClick={() => {
                    setSelectedId(id);
                    setChargeCountClickedMonitoringExecuteButton(
                      row.succeedCount
                    );
                    setAcquireBookOpen(true);
                  }}
                  disabled={isReadOnly || !isActionEnabled(row.status)}
                >
                  モニタリング
                </Button>
              </ReadOnlyTooltip>
            ) : null}
            <OutputMenu
              disabled={!isActionEnabled(row.status)}
              requestId={id.valueOf() as number}
              taskId={row.taskId}
              identificationName={row.identificationName}
              isReadOnly={isReadOnly}
              isPropertyDetailOptionCredit={row.isPropertyDetailOptionCredit}
              isDmCredit={row.isDmCredit}
              onDownloadComplete={onDownloadComplete}
            />
            {showSendToSamboKun
              ? (() => {
                  const reason = getSamboKunSendDisableReason(
                    row.succeedCount,
                    maxPropertiesPerRequest,
                    isLimitError
                  );
                  const isDisabled = reason !== "ok";
                  const tooltipText = !row.hasTriggerPictureRequest
                    ? "不動産登記取得結果に紐づく解析のみ連携できます"
                    : isDisabled
                    ? samboKunSendDisabledTooltip[reason]
                    : "";
                  return (
                    <Tooltip
                      title={tooltipText}
                      placement="top"
                      arrow
                      // limit-loading は空文字にマップされるため空タイトル時は
                      // 空ポッパー描画を避けるためホバーを無効化する
                      disableHoverListener={
                        (row.hasTriggerPictureRequest && !isDisabled) ||
                        tooltipText === ""
                      }
                    >
                      <span>
                        <Button
                          variant="outlined"
                          disabled={
                            !isActionEnabled(row.status) ||
                            !row.hasTriggerPictureRequest ||
                            isDisabled
                          }
                          onClick={() => {
                            onClickSendToSamboKun(id.valueOf() as number);
                          }}
                        >
                          参謀くんへ連携
                        </Button>
                      </span>
                    </Tooltip>
                  );
                })()
              : null}
          </Stack>
        );
      },
    },
  ];
};

/**
 * _FeedGrid
 * reload関数を親コンポーネントに外部公開するために、関数名にはアンダースコアをつけている
 * @param _props
 * @param ref
 */
const _FeedGrid: ForwardRefRenderFunction<{ reload: () => void }, IProps> = (
  _props,
  ref
) => {
  const {
    setOpenMonitoringExecuteConfirmDialog,
    setChargeCountClickedMonitoringExecuteButton,
    setSelectedId,
  } = _props;
  const isReadOnly = useReadOnlyUser();
  const { realEstateRegistrationMonitoring } = useFeatureFlags();
  const showSendToSamboKun = useShowSendToSamboKun();
  const { maxPropertiesPerRequest, isError: isLimitError } = useSamboKunLimit({
    enabled: showSendToSamboKun,
  });
  const [samboKunDialogRowId, setSamboKunDialogRowId] = useState<number | null>(
    null
  );
  // LaunchDarkly フラグや SSO 連携状態がランタイムで OFF に変わった場合、
  // 開きっぱなしのダイアログから送信できてしまわないよう state を強制クリアする
  useEffect(() => {
    if (!showSendToSamboKun) {
      setSamboKunDialogRowId(null);
    }
  }, [showSendToSamboKun]);
  const userData = useContext(userDataContext);
  const isSharableUser = Boolean(userData.data.isSharable);
  const selfAcquiredInitialState =
    buildSelfAcquiredInitialState(isSharableUser);

  let rows: OwnerInfoListGridRow[] = [];
  const { apiClient } = useApiClient();

  const fetcher = async (url: string): Promise<OwnerInfoListAPIResponse> => {
    const { data } = await apiClient.get<OwnerInfoListAPIResponse>(url);
    validateOwnerAnalyzeListContract(data, url);
    return data;
  };

  const { data, isLoading, mutate, error } = useSWR<
    OwnerInfoListAPIResponse,
    AxiosError
  >("/owner-analyze/", fetcher, {
    revalidateOnFocus: true,
    // NOTE: バックエンドの120秒制限に合わせる
    errorRetryInterval: 120_000,
  });

  const colDef = makeGridColDef(
    realEstateRegistrationMonitoring as boolean,
    isReadOnly,
    isSharableUser,
    setSelectedId,
    setOpenMonitoringExecuteConfirmDialog,
    setChargeCountClickedMonitoringExecuteButton,
    () => {
      mutate().catch(() => {
        // エラーハンドリングは既にuseSWRで行われているため、ここでは何もしない
      });
    },
    showSendToSamboKun,
    (rowId) => {
      setSamboKunDialogRowId(rowId);
    },
    maxPropertiesPerRequest,
    isLimitError
  );

  if (
    error &&
    (error.response?.status ?? HttpStatusCode.InternalServerError) >=
      HttpStatusCode.InternalServerError
  ) {
    SendToTrackingService(error);
  }

  // refを使って外部公開する関数を定義する
  useImperativeHandle(
    ref,
    () => {
      return {
        // mutate()を使って再読み込みを行う
        async reload() {
          await mutate().then((r) => {
            if (r) rows = r.list;
          });
        },
      };
    },
    []
  );

  if (isLoading || data === undefined) {
    return (
      <Stack alignItems="center">
        <CircularProgress sx={{ my: "20px" }} />
      </Stack>
    );
  }

  rows = data.list;

  return (
    <>
      <StyledDataGrid
        stickyHeader={true}
        autoHeight
        disableSelectionOnClick={true}
        rows={rows}
        columns={colDef}
        initialState={selfAcquiredInitialState}
        sortModel={[
          {
            field: "id",
            sort: "asc",
          },
        ]}
      />
      {showSendToSamboKun && samboKunDialogRowId != null && (
        <SendToSamboKunDialog
          open={samboKunDialogRowId != null}
          onClose={() => {
            setSamboKunDialogRowId(null);
          }}
          target="owner"
          analyzeRequestId={samboKunDialogRowId}
        />
      )}
    </>
  );
};

/**
 * FeedGrid
 * forwardRefを使ってreload()を外部から呼び出せるようにしている
 */
export const FeedGrid = forwardRef<{ reload: () => void }, IProps>(_FeedGrid);
